/**
 * ML Improvement Dashboard - PRD-3619
 * Performance Learning visualization with model metrics, patterns, and CTR trends
 */

import React, { useState, useEffect } from 'react';
import { 
  getMLDashboard, 
  triggerTraining, 
  getCopySuggestions,
  MLDashboardData, 
  PatternInsight,
  CopyAnalysis 
} from '../lib/mlMetricsApi';

// Pattern type badge colors
const patternColors: Record<string, string> = {
  keyword: 'bg-blue-100 text-blue-800',
  emotion: 'bg-purple-100 text-purple-800',
  length: 'bg-green-100 text-green-800',
  punctuation: 'bg-orange-100 text-orange-800',
};

// Status badge colors
const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  needs_data: 'bg-yellow-100 text-yellow-800',
  training: 'bg-blue-100 text-blue-800',
};

export default function MLImprovementDashboard() {
  const [dashboard, setDashboard] = useState<MLDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [trainingStatus, setTrainingStatus] = useState<string | null>(null);
  const [copyText, setCopyText] = useState('');
  const [copyAnalysis, setCopyAnalysis] = useState<CopyAnalysis | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    setLoading(true);
    try {
      const data = await getMLDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleTriggerTraining() {
    try {
      const result = await triggerTraining();
      setTrainingStatus(result.message);
      setTimeout(() => setTrainingStatus(null), 5000);
    } catch (error) {
      console.error('Failed to trigger training:', error);
    }
  }

  async function handleAnalyzeCopy() {
    if (!copyText.trim()) return;
    setAnalyzing(true);
    try {
      const analysis = await getCopySuggestions(copyText);
      setCopyAnalysis(analysis);
    } catch (error) {
      console.error('Failed to analyze copy:', error);
    } finally {
      setAnalyzing(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load ML dashboard data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              🧠 ML Performance Learning
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              AI-powered copy optimization with continuous learning
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[dashboard.learning_status]}`}>
              {dashboard.learning_status === 'active' ? '✅ Learning Active' : 
               dashboard.learning_status === 'training' ? '🔄 Training...' : '⚠️ Needs Data'}
            </span>
            <button
              onClick={handleTriggerTraining}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              🚀 Trigger Training
            </button>
          </div>
        </div>
        {trainingStatus && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-blue-800">{trainingStatus}</p>
          </div>
        )}
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <MetricCard
          title="30-Day CTR Improvement"
          value={`+${dashboard.ctr_improvement_30d.toFixed(1)}%`}
          subtitle="vs baseline"
          icon="📈"
          color="green"
        />
        <MetricCard
          title="Total Learning Samples"
          value={dashboard.total_samples.toLocaleString()}
          subtitle={`+${dashboard.samples_last_7_days} this week`}
          icon="📊"
          color="blue"
        />
        <MetricCard
          title="Approval Model Accuracy"
          value={`${((dashboard.approval_model.accuracy || 0) * 100).toFixed(0)}%`}
          subtitle={`AUC: ${((dashboard.approval_model.auc_score || 0)).toFixed(2)}`}
          icon="🎯"
          color="purple"
        />
        <MetricCard
          title="Quality Model MSE"
          value={(dashboard.quality_model.mse || 0).toFixed(4)}
          subtitle="Lower is better"
          icon="⚡"
          color="orange"
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Top Patterns */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            🏆 Top Performing Patterns
          </h2>
          <div className="space-y-3">
            {dashboard.top_patterns.map((pattern, index) => (
              <PatternCard key={index} pattern={pattern} rank={index + 1} />
            ))}
          </div>
        </div>

        {/* Feature Importance */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            📊 Feature Importance
          </h2>
          <div className="space-y-3">
            {Object.entries(dashboard.feature_importance)
              .sort(([, a], [, b]) => b - a)
              .map(([feature, importance]) => (
                <FeatureBar key={feature} feature={feature} importance={importance} />
              ))}
          </div>
        </div>
      </div>

      {/* CTR Trend Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          📈 CTR Improvement Over Time (30 Days)
        </h2>
        <div className="h-64 flex items-end gap-1">
          {dashboard.ctr_trend.map((day, index) => (
            <div key={index} className="flex-1 flex flex-col items-center group relative">
              <div className="w-full flex flex-col gap-0.5">
                <div 
                  className="w-full bg-blue-500 rounded-t"
                  style={{ height: `${day.ml_ctr * 2500}px` }}
                  title={`ML: ${(day.ml_ctr * 100).toFixed(2)}%`}
                />
                <div 
                  className="w-full bg-gray-300 rounded-t"
                  style={{ height: `${day.baseline_ctr * 2500}px` }}
                  title={`Baseline: ${(day.baseline_ctr * 100).toFixed(2)}%`}
                />
              </div>
              {/* Tooltip on hover */}
              <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                <div>{day.date}</div>
                <div>ML: {(day.ml_ctr * 100).toFixed(2)}%</div>
                <div>Base: {(day.baseline_ctr * 100).toFixed(2)}%</div>
                <div className="text-green-400">+{day.improvement_pct.toFixed(1)}%</div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>{dashboard.ctr_trend[0]?.date}</span>
          <div className="flex gap-4">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-blue-500 rounded"></span> ML Copy
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-gray-300 rounded"></span> Baseline
            </span>
          </div>
          <span>{dashboard.ctr_trend[dashboard.ctr_trend.length - 1]?.date}</span>
        </div>
      </div>

      {/* Copy Analyzer */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          ✍️ Copy Analyzer - Get ML Suggestions
        </h2>
        <div className="flex gap-4 mb-4">
          <input
            type="text"
            value={copyText}
            onChange={(e) => setCopyText(e.target.value)}
            placeholder="Enter your ad copy to analyze..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          <button
            onClick={handleAnalyzeCopy}
            disabled={analyzing || !copyText.trim()}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {analyzing ? '🔄 Analyzing...' : '🔍 Analyze'}
          </button>
        </div>

        {copyAnalysis && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex gap-6 mb-4">
              <div>
                <span className="text-sm text-gray-500">Quality Score</span>
                <div className="text-2xl font-bold text-blue-600">
                  {(copyAnalysis.quality_score * 100).toFixed(0)}%
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-500">Approval Probability</span>
                <div className="text-2xl font-bold text-green-600">
                  {(copyAnalysis.approval_probability * 100).toFixed(0)}%
                </div>
              </div>
            </div>
            
            {copyAnalysis.suggestions.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                  💡 Suggestions to Improve
                </h3>
                <ul className="space-y-2">
                  {copyAnalysis.suggestions.map((suggestion, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${patternColors[suggestion.type] || 'bg-gray-100 text-gray-800'}`}>
                        {suggestion.type}
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">{suggestion.suggestion}</span>
                      <span className="text-green-600 font-medium whitespace-nowrap">{suggestion.impact}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {copyAnalysis.suggestions.length === 0 && (
              <p className="text-green-600 font-medium">✅ Great copy! No major improvements suggested.</p>
            )}
          </div>
        )}
      </div>

      {/* Model Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 dark:text-white mb-2">Approval Classifier</h3>
          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <p>Version: {dashboard.approval_model.model_version}</p>
            <p>Training Samples: {dashboard.approval_model.samples_count.toLocaleString()}</p>
            <p>Last Training: {new Date(dashboard.last_training).toLocaleString()}</p>
            <p>Next Training: {new Date(dashboard.next_training).toLocaleString()}</p>
          </div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
          <h3 className="font-medium text-gray-900 dark:text-white mb-2">Quality Regressor</h3>
          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <p>Version: {dashboard.quality_model.model_version}</p>
            <p>Training Samples: {dashboard.quality_model.samples_count.toLocaleString()}</p>
            <p>MSE: {(dashboard.quality_model.mse || 0).toFixed(4)}</p>
            <p>Algorithm: GradientBoostingRegressor</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Metric Card Component
function MetricCard({ title, value, subtitle, icon, color }: {
  title: string;
  value: string;
  subtitle: string;
  icon: string;
  color: 'green' | 'blue' | 'purple' | 'orange';
}) {
  const bgColors = {
    green: 'bg-green-50 border-green-200',
    blue: 'bg-blue-50 border-blue-200',
    purple: 'bg-purple-50 border-purple-200',
    orange: 'bg-orange-50 border-orange-200',
  };
  
  const textColors = {
    green: 'text-green-700',
    blue: 'text-blue-700',
    purple: 'text-purple-700',
    orange: 'text-orange-700',
  };

  return (
    <div className={`rounded-xl border p-4 ${bgColors[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-2xl">{icon}</span>
        <span className="text-sm font-medium text-gray-600">{title}</span>
      </div>
      <div className={`text-3xl font-bold ${textColors[color]}`}>{value}</div>
      <div className="text-sm text-gray-500 mt-1">{subtitle}</div>
    </div>
  );
}

// Pattern Card Component
function PatternCard({ pattern, rank }: { pattern: PatternInsight; rank: number }) {
  return (
    <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
      <span className="text-lg font-bold text-gray-400">#{rank}</span>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${patternColors[pattern.pattern_type]}`}>
            {pattern.pattern_type}
          </span>
          <span className="font-medium text-gray-900 dark:text-white">{pattern.pattern_value}</span>
        </div>
        <div className="text-sm text-gray-500 mt-1">
          {pattern.sample_size} samples • {(pattern.confidence * 100).toFixed(0)}% confidence
        </div>
      </div>
      <div className="text-right">
        <div className="text-lg font-bold text-green-600">+{pattern.lift_vs_baseline.toFixed(0)}%</div>
        <div className="text-xs text-gray-500">CTR lift</div>
      </div>
    </div>
  );
}

// Feature Bar Component
function FeatureBar({ feature, importance }: { feature: string; importance: number }) {
  const formattedName = feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  return (
    <div className="flex items-center gap-3">
      <span className="w-32 text-sm text-gray-600 dark:text-gray-400 truncate">{formattedName}</span>
      <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-4 overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500"
          style={{ width: `${importance * 100}%` }}
        />
      </div>
      <span className="w-12 text-sm font-medium text-gray-700 dark:text-gray-300 text-right">
        {(importance * 100).toFixed(0)}%
      </span>
    </div>
  );
}
